/**
 * Welcome Titles API
 *
 * Welcome scene and title management endpoints.
 * Corresponds to backend /v1/admin/welcome/*
 */

import apiClient from './client';
import type { GenerateTitlesRequest, GenerateTitlesResponse } from '@/features/welcome-titles/types';

// ============================================================================
// Types
// ============================================================================

export type SceneCondition =
  | { type: 'date'; month: number; day: number; calendar: 'gregorian' | 'lunar' }
  | { type: 'date_range'; start: [number, number]; end: [number, number]; calendar: 'gregorian' | 'lunar' }
  | { type: 'time_range'; start_hour: number; end_hour: number }
  | { type: 'weekday'; days: number[] };

export interface WelcomeTitle {
  id: string;
  scene_id: string;
  text_zh: string;
  text_zh_hant: string;
  text_en: string;
  text_zh_noname: string;
  text_zh_hant_noname: string;
  text_en_noname: string;
  is_enabled: boolean;
  sort_order: number;
}

export interface WelcomeScene {
  id: string;
  scene_key: string;
  name: string;
  description: string | null;
  priority: number;
  is_default: boolean;
  is_enabled: boolean;
  condition: SceneCondition | null;
  titles?: WelcomeTitle[];
}

export interface WelcomeConfig {
  time_ranges: Record<string, { start: number; end: number }>;
  birthday_range_days: number;
  comeback_light_days: [number, number];
  comeback_deep_days: number;
  dedup_window: number;
}

export interface SceneCreateRequest {
  scene_key: string;
  name: string;
  priority: number;
  condition?: SceneCondition | null;
  description?: string | null;
  derive_eve?: boolean;
  derive_after?: boolean;
}

export interface SceneUpdateRequest {
  scene_key?: string;
  name?: string;
  description?: string | null;
  priority?: number;
  is_enabled?: boolean;
  condition?: SceneCondition | null;
}

export interface TitleCreateRequest {
  text_zh: string;
  text_zh_hant: string;
  text_en: string;
  text_zh_noname: string;
  text_zh_hant_noname: string;
  text_en_noname: string;
  is_enabled?: boolean;
  sort_order?: number;
}

export interface TitleUpdateRequest {
  text_zh?: string;
  text_zh_hant?: string;
  text_en?: string;
  text_zh_noname?: string;
  text_zh_hant_noname?: string;
  text_en_noname?: string;
  is_enabled?: boolean;
  sort_order?: number;
}

export interface PreviewResult {
  matched_scene: string | null;
  scene_key: string | null;
  is_default: boolean;
  titles_count: number;
}

export interface LunarConvertResult {
  gregorian_month: number;
  gregorian_day: number;
  year: number;
}

// ============================================================================
// API Client
// ============================================================================

export const welcomeApi = {
  // --- Scenes ---

  listScenes: async (includeDisabled = true): Promise<WelcomeScene[]> => {
    const response = await apiClient.get<WelcomeScene[]>('/welcome/scenes', {
      params: { include_disabled: includeDisabled },
    });
    return response.data;
  },

  getScene: async (sceneId: string): Promise<WelcomeScene> => {
    const response = await apiClient.get<WelcomeScene>(`/welcome/scenes/${sceneId}`);
    return response.data;
  },

  createScene: async (data: SceneCreateRequest): Promise<WelcomeScene> => {
    const response = await apiClient.post<WelcomeScene>('/welcome/scenes', data);
    return response.data;
  },

  updateScene: async (sceneId: string, data: SceneUpdateRequest): Promise<WelcomeScene> => {
    const response = await apiClient.put<WelcomeScene>(`/welcome/scenes/${sceneId}`, data);
    return response.data;
  },

  deleteScene: async (sceneId: string): Promise<void> => {
    await apiClient.delete(`/welcome/scenes/${sceneId}`);
  },

  // --- Titles ---

  listTitles: async (sceneId: string): Promise<WelcomeTitle[]> => {
    const response = await apiClient.get<WelcomeTitle[]>(`/welcome/scenes/${sceneId}/titles`);
    return response.data;
  },

  createTitle: async (sceneId: string, data: TitleCreateRequest): Promise<WelcomeTitle> => {
    const response = await apiClient.post<WelcomeTitle>(`/welcome/scenes/${sceneId}/titles`, data);
    return response.data;
  },

  updateTitle: async (titleId: string, data: TitleUpdateRequest): Promise<WelcomeTitle> => {
    const response = await apiClient.put<WelcomeTitle>(`/welcome/titles/${titleId}`, data);
    return response.data;
  },

  deleteTitle: async (titleId: string): Promise<void> => {
    await apiClient.delete(`/welcome/titles/${titleId}`);
  },

  reorderTitles: async (titleIds: string[]): Promise<void> => {
    await apiClient.post('/welcome/titles/reorder', { title_ids: titleIds });
  },

  // --- Config ---

  getConfig: async (): Promise<WelcomeConfig> => {
    const response = await apiClient.get<WelcomeConfig>('/welcome/config');
    return response.data;
  },

  updateConfig: async (config: Partial<WelcomeConfig>): Promise<{ status: string; version: number }> => {
    const response = await apiClient.put<{ status: string; version: number }>('/welcome/config', config);
    return response.data;
  },

  // --- Utilities ---

  preview: async (date: string): Promise<PreviewResult> => {
    const response = await apiClient.get<PreviewResult>('/welcome/preview', { params: { date } });
    return response.data;
  },

  lunarConvert: async (month: number, day: number, year: number): Promise<LunarConvertResult> => {
    const response = await apiClient.post<LunarConvertResult>('/welcome/lunar-convert', { month, day, year });
    return response.data;
  },

  composePrompt: async (data: { scene_key: string; scene_name: string; scene_description?: string; custom_prompt?: string; count?: number }): Promise<{ system_prompt: string; user_prompt: string }> =>
    (await apiClient.post('/welcome/compose-prompt', data)).data,

  generateTitles: async (data: GenerateTitlesRequest): Promise<GenerateTitlesResponse> =>
    (await apiClient.post('/welcome/generate-titles', data, { timeout: 120_000 })).data,
};
